import { useState, useRef, useEffect, useMemo } from 'react'
import { useParams, useNavigate, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTaskStore } from '../store/taskStore'
import { addPhoto } from '../api/submissions'
import ShopPicker, { type ShopSelection } from '../components/ShopPicker'
import CameraCapture from '../components/CameraCapture'
import SuccessOverlay from '../components/SuccessOverlay'
import { getLocation } from '../utils/geolocation'

const NOT_FOUND_REASON_KEYS = [
  'out_of_stock',
  'shop_closed',
  'wrong_location',
  'discontinued',
  'other',
] as const

// Camera icon SVG
function CameraIcon() {
  return (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
      <circle cx="12" cy="12" r="3.2" fill="rgba(255,255,255,0.7)" />
      <path
        d="M9 2L7.17 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2h-3.17L15 2H9zm3 15c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5z"
        fill="rgba(255,255,255,0.7)"
      />
    </svg>
  )
}

export default function SubmissionPage() {
  const { t } = useTranslation(['worker', 'common'])
  const { assignmentId, itemId } = useParams<{ assignmentId: string; itemId: string }>()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentTask, submissions, submitItem } = useTaskStore()

  const item = currentTask?.items.find((i) => i.id === itemId)
  const existingSubmission = submissions[itemId ?? '']

  // Pre-select type from navigation state (from TaskDetailPage)
  const preselectType = (location.state as { preselect?: string } | null)?.preselect
  const [submissionType, setSubmissionType] = useState<'found' | 'not_found'>(
    preselectType === 'not_found' ? 'not_found' : 'found'
  )
  const [shopSelection, setShopSelection] = useState<ShopSelection | null>(null)
  const [shopAutoFilled, setShopAutoFilled] = useState(false)
  const [stallSection, setStallSection] = useState('')
  const [priceUzs, setPriceUzs] = useState('')
  const [currency, setCurrency] = useState<'UZS' | 'USD'>('UZS')
  const [productName, setProductName] = useState(item?.product_name || '')
  const [productDescription, setProductDescription] = useState('')
  const [productBrand, setProductBrand] = useState('')
  const [comment, setComment] = useState('')
  const [notFoundReason, setNotFoundReason] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [showMoreDetails, setShowMoreDetails] = useState(false)
  const [showCamera, setShowCamera] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  const [gpsLat, setGpsLat] = useState<number | null>(null)
  const [gpsLng, setGpsLng] = useState<number | null>(null)
  const [gpsLoading, setGpsLoading] = useState(false)
  const [gpsError, setGpsError] = useState<string | null>(null)

  // Auto-capture GPS on mount
  useEffect(() => {
    setGpsLoading(true)
    getLocation()
      .then(({ latitude, longitude }) => {
        setGpsLat(latitude)
        setGpsLng(longitude)
      })
      .catch((e: unknown) => {
        setGpsError(e instanceof Error ? e.message : t('common:error.failedToLoad'))
      })
      .finally(() => setGpsLoading(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Pre-populate from existing submission
  useEffect(() => {
    if (!existingSubmission) return
    setSubmissionType(existingSubmission.submission_type)
    if (existingSubmission.price_usd != null) {
      setCurrency('USD')
      setPriceUzs(String(existingSubmission.price_usd))
    } else {
      setCurrency('UZS')
      setPriceUzs(existingSubmission.price_uzs != null ? String(existingSubmission.price_uzs) : '')
    }
    setStallSection(existingSubmission.stall_section ?? '')
    setProductName(existingSubmission.product_name ?? item?.product_name ?? '')
    setProductBrand(existingSubmission.product_brand ?? '')
    setComment(existingSubmission.comment ?? '')
    if (existingSubmission.shop_name) {
      setShopSelection({
        shopName: existingSubmission.shop_name,
        selectedMerchantId: existingSubmission.selected_merchant_id,
        shopLat:
          existingSubmission.shop_latitude != null
            ? Number(existingSubmission.shop_latitude)
            : undefined,
        shopLng:
          existingSubmission.shop_longitude != null
            ? Number(existingSubmission.shop_longitude)
            : undefined,
      })
    }
  }, [itemId]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fill shop from other submissions in same assignment
  useEffect(() => {
    if (shopSelection || existingSubmission?.shop_name) return
    const otherEntry = Object.entries(submissions).find(
      ([id, sub]) => id !== itemId && sub.shop_name
    )
    if (otherEntry) {
      const [, sub] = otherEntry
      setShopSelection({
        shopName: sub.shop_name!,
        selectedMerchantId: sub.selected_merchant_id,
        shopLat: sub.shop_latitude != null ? Number(sub.shop_latitude) : undefined,
        shopLng: sub.shop_longitude != null ? Number(sub.shop_longitude) : undefined,
      })
      setShopAutoFilled(true)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Create object URLs for preview
  const photoPreviews = useMemo(() => photos.map((f) => URL.createObjectURL(f)), [photos])
  useEffect(() => {
    return () => {
      photoPreviews.forEach((url) => URL.revokeObjectURL(url))
    }
  }, [photoPreviews])

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) setPhotos((prev) => [...prev, ...Array.from(e.target.files!)])
  }

  const removePhoto = (index: number) => {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSubmit = async () => {
    if (!assignmentId || !itemId) return
    setIsSubmitting(true)
    try {
      const effectiveComment =
        submissionType === 'not_found' && notFoundReason
          ? comment.trim()
            ? `[${notFoundReason}] ${comment.trim()}`
            : notFoundReason
          : comment || undefined

      const parsedPrice = priceUzs ? parseFloat(priceUzs) : undefined
      const submission = await submitItem({
        assignment_id: assignmentId,
        task_item_id: itemId,
        submission_type: submissionType,
        price_uzs: currency === 'UZS' ? parsedPrice : undefined,
        price_usd: currency === 'USD' ? parsedPrice : undefined,
        shop_name: shopSelection?.shopName || undefined,
        stall_section: stallSection || undefined,
        shop_latitude: gpsLat ?? shopSelection?.shopLat,
        shop_longitude: gpsLng ?? shopSelection?.shopLng,
        shop_phone: shopSelection?.shopPhone,
        shop_address: shopSelection?.shopAddress,
        shop_category: shopSelection?.shopCategory,
        shop_description: shopSelection?.shopDescription,
        selected_merchant_id: shopSelection?.selectedMerchantId,
        product_name: productName || undefined,
        product_brand: productBrand || undefined,
        product_description: productDescription || undefined,
        comment: effectiveComment,
      })
      for (const photo of photos) {
        await addPhoto(submission.id, photo)
      }
      setShowSuccess(true)
      setTimeout(() => navigate(-1), 1800)
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : t('common:error.generic'))
      setIsSubmitting(false)
    }
  }

  const inputStyle: React.CSSProperties = {
    background: 'var(--app-card-bg)',
    color: 'var(--tg-theme-text-color)',
    WebkitTextFillColor: 'var(--tg-theme-text-color)',
    caretColor: 'var(--tg-theme-text-color)',
    border: '1.5px solid transparent',
    borderRadius: '14px',
    padding: '12px 14px',
    width: '100%',
    fontSize: '16px',
    outline: 'none',
    boxSizing: 'border-box',
  }

  const labelStyle: React.CSSProperties = {
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    color: 'var(--tg-theme-text-color)',
    opacity: 0.5,
    marginBottom: '6px',
    display: 'block',
  }

  const existingPhotoUrls = existingSubmission?.photos ?? []
  const hasPhotos = existingPhotoUrls.length + photoPreviews.length > 0

  const saveDisabled =
    isSubmitting ||
    (submissionType === 'found' && !shopSelection) ||
    (submissionType === 'not_found' && !notFoundReason)

  return (
    <div className="min-h-screen safe-area-bottom" style={{ background: 'var(--app-page-bg)' }}>
      {showSuccess && (
        <SuccessOverlay
          potentialCoins={currentTask?.reward_per_item_coins}
          submissionType={submissionType}
        />
      )}

      {/* ── Header ─────────────────────────────────────────── */}
      <div
        className="flex items-center gap-3 px-4 pt-4 pb-3"
        style={{ background: 'var(--app-card-bg)' }}
      >
        <button
          className="text-xl leading-none opacity-50 flex-shrink-0"
          style={{ color: 'var(--tg-theme-text-color)' }}
          onClick={() => navigate(-1)}
        >
          ←
        </button>
        <h1 className="font-bold text-base truncate" style={{ color: 'var(--tg-theme-text-color)' }}>
          {item?.product_name || t('submission.title')}
        </h1>
      </div>

      {/* ── Product header ─────────────────────────────────── */}
      {item && (
        <div style={{ background: 'var(--app-card-bg)', borderBottom: '1px solid var(--app-page-bg)' }}>
          {(() => {
            const coverUrl = item.item_images?.[0]?.image_url ?? item.item_image_url
            return coverUrl ? (
              <div className="w-full flex items-center justify-center" style={{ height: 220, background: 'var(--app-page-bg)' }}>
                <img src={coverUrl} alt="" className="h-full w-full object-contain" />
              </div>
            ) : (
              <div className="w-full flex items-center justify-center" style={{ height: 160, background: 'var(--app-page-bg)' }}>
                <span className="font-bold text-6xl opacity-30" style={{ color: 'var(--tg-theme-button-color)' }}>
                  {item.product_name.charAt(0).toUpperCase()}
                </span>
              </div>
            )
          })()}
          <div className="px-4 py-3">
            <p className="font-bold text-lg leading-tight" style={{ color: 'var(--tg-theme-text-color)' }}>
              {item.product_name}
            </p>
            {item.product_description && (
              <p className="text-sm opacity-50 mt-0.5 line-clamp-2" style={{ color: 'var(--tg-theme-text-color)' }}>
                {item.product_description}
              </p>
            )}
          </div>
        </div>
      )}

      <div className="p-4 space-y-4">
        {/* ── Found / Not Found toggle ──────────────────────── */}
        <div className="flex rounded-2xl overflow-hidden" style={{ background: 'var(--app-card-bg)' }}>
          {(['found', 'not_found'] as const).map((type) => (
            <button
              key={type}
              className="flex-1 py-2.5 text-sm font-semibold"
              style={{
                background: submissionType === type ? 'var(--tg-theme-button-color)' : 'transparent',
                color:
                  submissionType === type
                    ? 'var(--tg-theme-button-text-color)'
                    : 'var(--tg-theme-text-color)',
              }}
              onClick={() => setSubmissionType(type)}
            >
              {type === 'found' ? t('submission.found') : t('submission.notFound')}
            </button>
          ))}
        </div>

        {submissionType === 'found' && (
          <div className="space-y-4">
            {/* ── Photo zone ─────────────────────────────────── */}
            <div>
              {showCamera && (
                <CameraCapture
                  onCapture={(file) => {
                    setPhotos((prev) => [...prev, file])
                    setShowCamera(false)
                  }}
                  onClose={() => setShowCamera(false)}
                />
              )}
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handlePhotoChange}
              />
              {!hasPhotos ? (
                <div
                  className="w-full rounded-2xl flex flex-col items-center justify-center gap-3"
                  style={{ height: 180, background: '#1e2540' }}
                >
                  <div className="flex gap-3">
                    <button
                      className="flex flex-col items-center gap-1.5"
                      style={{ background: 'none', border: 'none' }}
                      onClick={() => setShowCamera(true)}
                    >
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(255,255,255,0.12)' }}
                      >
                        <CameraIcon />
                      </div>
                      <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 500 }}>
                        {t('submission.takePhoto')}
                      </span>
                    </button>
                    <button
                      className="flex flex-col items-center gap-1.5"
                      style={{ background: 'none', border: 'none' }}
                      onClick={() => fileRef.current?.click()}
                    >
                      <div
                        className="w-14 h-14 rounded-full flex items-center justify-center"
                        style={{ background: 'rgba(255,255,255,0.12)' }}
                      >
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                          <rect x="3" y="3" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                          <rect x="14" y="3" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                          <rect x="3" y="14" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                          <rect x="14" y="14" width="7" height="7" rx="1" fill="rgba(255,255,255,0.7)" />
                        </svg>
                      </div>
                      <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: 12, fontWeight: 500 }}>
                        {t('submission.gallery')}
                      </span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
                  {existingPhotoUrls.map((p) => (
                    <img
                      key={p.id}
                      src={p.thumbnail_url || `/api/v1/media/${p.file_path}`}
                      className="w-20 h-20 rounded-xl object-cover flex-shrink-0"
                      alt="submission photo"
                    />
                  ))}
                  {photoPreviews.map((url, i) => (
                    <div key={url} className="relative flex-shrink-0">
                      <img src={url} className="w-20 h-20 rounded-xl object-cover" alt="preview" />
                      <button
                        className="absolute -top-1.5 -right-1.5 w-5 h-5 rounded-full flex items-center justify-center text-xs text-white"
                        style={{ background: '#e53935' }}
                        onClick={() => removePhoto(i)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                  <button
                    className="w-20 h-20 rounded-xl flex items-center justify-center flex-shrink-0 text-2xl font-bold"
                    style={{
                      background: '#1e2540',
                      color: 'rgba(255,255,255,0.5)',
                      border: 'none',
                    }}
                    onClick={() => setShowCamera(true)}
                  >
                    +
                  </button>
                </div>
              )}
            </div>

            {/* ── Price ────────────────────────────────────── */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label style={{ ...labelStyle, marginBottom: 0 }}>{t('submission.price')}</label>
                <div className="flex rounded-xl overflow-hidden" style={{ background: 'var(--app-card-bg)' }}>
                  {(['UZS', 'USD'] as const).map((c) => (
                    <button
                      key={c}
                      type="button"
                      className="px-3 py-1 text-xs font-semibold"
                      style={{
                        background: currency === c ? 'var(--tg-theme-button-color)' : 'transparent',
                        color: currency === c ? 'var(--tg-theme-button-text-color)' : 'var(--tg-theme-hint-color)',
                      }}
                      onClick={() => setCurrency(c)}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
              <input
                style={{
                  ...inputStyle,
                  fontSize: '20px',
                  fontWeight: '600',
                  border: '1.5px solid var(--tg-theme-button-color)',
                }}
                placeholder="0"
                type="number"
                inputMode="numeric"
                value={priceUzs}
                onChange={(e) => setPriceUzs(e.target.value)}
              />
            </div>

            {/* ── Shop ─────────────────────────────────────── */}
            <div>
              <label style={labelStyle}>{t('submission.shop')}</label>
              <ShopPicker
                value={shopSelection}
                onChange={(s) => {
                  setShopSelection(s)
                  setShopAutoFilled(false)
                }}
                onClear={() => {
                  setShopSelection(null)
                  setShopAutoFilled(false)
                }}
              />
              {shopAutoFilled && shopSelection && (
                <p className="text-xs mt-1 font-medium" style={{ color: '#22c55e' }}>
                  {t('submission.autoFilledShop')}
                </p>
              )}
            </div>

            {/* ── GPS status ───────────────────────────────── */}
            <div>
              {gpsLoading ? (
                <div className="flex items-center gap-2 py-2 px-3 rounded-xl text-xs font-medium" style={{ background: 'var(--app-card-bg)', color: 'var(--tg-theme-hint-color)' }}>
                  {t('common:gps.getting')}
                </div>
              ) : gpsLat != null ? (
                <div className="flex items-center justify-between py-2 px-3 rounded-xl text-xs font-medium" style={{ background: '#22c55e18', color: '#22c55e' }}>
                  <span>{t('common:gps.captured', { lat: gpsLat.toFixed(4), lng: gpsLng!.toFixed(4) })}</span>
                  <button
                    className="ml-2 opacity-60 hover:opacity-100"
                    style={{ background: 'none', border: 'none', color: '#22c55e', fontSize: 11 }}
                    onClick={() => {
                      setGpsLoading(true)
                      setGpsError(null)
                      getLocation()
                        .then(({ latitude, longitude }) => { setGpsLat(latitude); setGpsLng(longitude) })
                        .catch((e: unknown) => setGpsError(e instanceof Error ? e.message : t('common:error.failedToLoad')))
                        .finally(() => setGpsLoading(false))
                    }}
                  >
                    {t('common:gps.retry')}
                  </button>
                </div>
              ) : gpsError ? (
                <div className="flex items-center justify-between py-2 px-3 rounded-xl text-xs font-medium" style={{ background: '#ef444418', color: '#ef4444' }}>
                  <span>{t('common:gps.error', { message: gpsError })}</span>
                  <button
                    className="ml-2 underline"
                    style={{ background: 'none', border: 'none', color: '#ef4444', fontSize: 11 }}
                    onClick={() => {
                      setGpsLoading(true)
                      setGpsError(null)
                      getLocation()
                        .then(({ latitude, longitude }) => { setGpsLat(latitude); setGpsLng(longitude) })
                        .catch((e: unknown) => setGpsError(e instanceof Error ? e.message : t('common:error.failedToLoad')))
                        .finally(() => setGpsLoading(false))
                    }}
                  >
                    {t('common:retry')}
                  </button>
                </div>
              ) : null}
            </div>

            {/* ── Stall / section ──────────────────────────── */}
            <div>
              <label style={labelStyle}>{t('submission.stallSection')}</label>
              <input
                style={inputStyle}
                placeholder={t('submission.stallSectionPlaceholder')}
                value={stallSection}
                onChange={(e) => setStallSection(e.target.value)}
              />
            </div>

            {/* ── Collapsible more details ──────────────────── */}
            <button
              className="text-sm font-medium flex items-center gap-1"
              style={{ color: 'var(--tg-theme-link-color)', background: 'none', border: 'none' }}
              onClick={() => setShowMoreDetails((v) => !v)}
            >
              <span>{showMoreDetails ? '▼' : '▶'}</span>
              <span>{t('submission.moreDetails')}</span>
            </button>

            {showMoreDetails && (
              <div className="space-y-3">
                <input
                  style={inputStyle}
                  placeholder={t('submission.productName')}
                  value={productName}
                  onChange={(e) => setProductName(e.target.value)}
                />
                <textarea
                  style={{ ...inputStyle, minHeight: '56px', resize: 'none' }}
                  placeholder={t('submission.description')}
                  value={productDescription}
                  onChange={(e) => setProductDescription(e.target.value)}
                />
                <input
                  style={inputStyle}
                  placeholder={t('submission.brand')}
                  value={productBrand}
                  onChange={(e) => setProductBrand(e.target.value)}
                />
                <textarea
                  style={{ ...inputStyle, minHeight: '56px', resize: 'none' }}
                  placeholder={t('submission.comment')}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />
              </div>
            )}
          </div>
        )}

        {submissionType === 'not_found' && (
          <div className="space-y-3">
            <div>
              <label style={labelStyle}>{t('submission.reason')}</label>
              <select
                style={{
                  ...inputStyle,
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  cursor: 'pointer',
                  color: notFoundReason ? 'var(--tg-theme-text-color)' : 'var(--tg-theme-hint-color)',
                  WebkitTextFillColor: notFoundReason
                    ? 'var(--tg-theme-text-color)'
                    : 'var(--tg-theme-hint-color)',
                } as React.CSSProperties}
                value={notFoundReason}
                onChange={(e) => setNotFoundReason(e.target.value)}
              >
                <option value="">{t('submission.selectReason')}</option>
                {NOT_FOUND_REASON_KEYS.map((key) => (
                  <option key={key} value={key}>
                    {t(`submission.notFoundReasons.${key}`)}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              style={{ ...inputStyle, minHeight: '80px', resize: 'none' }}
              placeholder={t('submission.additionalDetails')}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
            />
          </div>
        )}

        {/* ── Save button ─────────────────────────────────── */}
        <button
          className="w-full py-4 rounded-2xl font-bold text-base disabled:opacity-50 flex items-center justify-center gap-2 mb-6"
          style={{
            background: saveDisabled ? 'var(--tg-theme-hint-color)' : 'var(--tg-theme-button-color)',
            color: 'var(--tg-theme-button-text-color)',
          }}
          disabled={saveDisabled}
          onClick={handleSubmit}
        >
          {isSubmitting ? t('submission.submitting') : (
            <>
              {t('submission.saveAndNext')}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M9 18l6-6-6-6" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </>
          )}
        </button>
      </div>
    </div>
  )
}
